import React, { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import Image, { type ImageProps } from 'next/image';
import styled, { css } from 'styled-components';
import type { SpaceProps } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import media from 'styled-media-query';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import styledSpaces from '@firsttable/web-theme/styledSpaces';
import { awsImage } from '@firsttable/functions';
import { dayMonthYear } from '@firsttable/functions/src/dateWithDayJs';
import Stars from '@discover-components/restaurant/rating/stars';
import { Flex, Box, Text, Avatar } from '@firsttable/web-components/atoms';
import IconQuote from '@firsttable/web-components/atoms/Icon/IconQuote';
import ReadMoreBlock from '@firsttable/web-components/molecules/ReadMoreBlock';
import type { ReviewData } from './types';

const convertNewLinesToBr = (text?: string | null) =>
  (text || '')
    .split('\n')
    .filter(Boolean)
    .map((line, index, array) => (
      <div key={index}>
        {line}
        {index < array.length - 1 && (
          <>
            <br />
            <br />
          </>
        )}
      </div>
    ));

const Wrapper = styled(Flex)<{ $card: boolean }>`
  ${({ $card }) =>
    $card &&
    media.greaterThan(bp.s)`
    position: relative;
    max-width: 590px;
    padding: 30px 40px 60px 0;

    &::before {
      content: " ";
      position: absolute;
      top: 10px;
      left: 13px;
      right: 11px;
      bottom: 13px;
      border-radius: 6px;
      transform: rotate(2deg) skew(-3deg, 0);
      ${themeGet('colorStyles.card.m')};
    }
  `};
`;

const IconQuoteBox = styled(Box)<{ $readMore: boolean }>`
  position: absolute;
  bottom: 46px;
  right: 67px;
  ${({ $readMore }) =>
    $readMore &&
    css`
      bottom: 28px;
    `}
`;

const RoundAvatar = styled.div`
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  z-index: 1;
  img {
    width: 100%;
    height: 100%;
  }
`;

const LoadingAvatarBackground = () => (
  <Box
    width="100%"
    pt="100%"
    bg="grayscale.300"
    borderRadius="50%"
    className="pulse-animation"
    position="absolute"
    top={0}
    left={0}
  />
);

export type ReviewCardProps = SpaceProps & {
  review: ReviewData;
  name?: string | null;
  comment?: string | null;
  date?: string | null;
  userType?: string | null;
  nameAccessory?: ReactNode;
  card?: boolean;
  showRatings?: boolean;
  showReadMore?: boolean;
  subTextPrefix?: string;
  ImageComponent?: ComponentType<ImageProps>;
};

export default function ReviewCard({
  review,
  name,
  comment,
  date,
  userType,
  nameAccessory,
  card = false,
  showRatings = true,
  showReadMore = true,
  subTextPrefix = 'Dined at ',
  ImageComponent = Image,
  ...rest
}: ReviewCardProps) {
  const [avatar, setAvatar] = useState(review.userAvatar ?? '');
  const isRestaurant = userType === 'Restaurant';
  const isStaff = userType === 'First Table';
  const restaurantTitle = review.restaurant?.title;
  const restaurantLogo = awsImage(
    review.restaurant?.logo?.url,
    100,
    100,
    'https://img.firsttable.net/'
  );
  const userAvatar = avatar.replace(/=36/gim, '=100').replace(/36x36/, '100x100');
  const currentAvatar = isRestaurant ? restaurantLogo : userAvatar;

  const parsedComment = useMemo(() => convertNewLinesToBr(comment), [comment]);

  const showOverallStars =
    !isRestaurant && !isStaff && Boolean(review.foodRating && review.serviceRating);
  const overallRating = showOverallStars
    ? ((review.foodRating ?? 0) + (review.serviceRating ?? 0)) / 2
    : 0;

  return (
    <Wrapper {...styledSpaces(rest)} $card={card}>
      <Flex position="relative">
        <Box mr={[12, 'm']} width={[32, 56]} mt={[-8, -5]} position="relative">
          <LoadingAvatarBackground />
          {currentAvatar && !isStaff ? (
            <RoundAvatar>
              <ImageComponent
                src={currentAvatar}
                alt={name ?? ''}
                className="img-fluid"
                width={56}
                height={56}
                onError={() => setAvatar('')}
                unoptimized
              />
            </RoundAvatar>
          ) : (
            <Avatar
              color={isStaff ? 'brand.blue' : 'grayscale.400'}
              width="100%"
              height="100%"
              preserveAspectRatio="xMidYMin meet"
            />
          )}
        </Box>
        <Box flex={1} mt={-6} maxWidth={card ? [null, 425] : null}>
          <Text
            fontSize={18}
            mb={[12, 'xs']}
            fontWeight="bold"
            style={{ textTransform: 'capitalize' }}>
            <Box as="span">
              {name || 'Visitor'} {isStaff && <> - First Table</>}
              {isRestaurant && <>- {restaurantTitle}</>}
            </Box>
            {nameAccessory}
          </Text>
          <Box ml={[-44, 0]}>
            <Text mt={-6} mb={12} color="grayscale.600">
              {isRestaurant || isStaff ? (
                dayMonthYear(date)
              ) : (
                <>
                  {restaurantTitle ? `${subTextPrefix}${restaurantTitle}` : 'Dined '}
                  {date && <Box as="span"> on {dayMonthYear(date)}</Box>}
                </>
              )}
            </Text>
            {showOverallStars && (
              <Box mb={7}>
                <Stars count={overallRating} />
              </Box>
            )}
            <Box mb={showRatings ? 20 : 0}>
              {showReadMore ? <ReadMoreBlock content={parsedComment} /> : parsedComment}
            </Box>
            {showRatings && (
              <Box color="grayscale.600" display={[null, 'flex']}>
                <Flex mr="35px">
                  <Box mr="19px">Food</Box>
                  <Stars count={review.foodRating} />
                </Flex>
                <Flex>
                  <Box mr="19px">Service</Box>
                  <Stars count={review.serviceRating} />
                </Flex>
              </Box>
            )}
          </Box>
        </Box>
      </Flex>
      {card && (
        <IconQuoteBox display={['none', 'block']} $readMore={showReadMore}>
          <IconQuote />
        </IconQuoteBox>
      )}
    </Wrapper>
  );
}
